import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Boxes,
  Download,
  FileCode2,
  FlaskConical,
  GitBranch,
  Search,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { TermWindow } from "@/components/organisms/studio/TermWindow";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { useEntity360, useEntitySearch } from "@/hooks/studio-platform";
import { encodeBase64Json } from "@/lib/studio/utils";
import type { EntityAction, EntitySearchItem } from "@/services/studio/types";

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  org: <Boxes className="h-4 w-4" />,
  object: <Boxes className="h-4 w-4" />,
  class: <FileCode2 className="h-4 w-4" />,
  trigger: <GitBranch className="h-4 w-4" />,
  flow: <Workflow className="h-4 w-4" />,
};

const PANEL_ICONS: Record<string, React.ReactNode> = {
  Schema: <Boxes className="h-4 w-4" />,
  Graph: <GitBranch className="h-4 w-4" />,
  "Code Risk": <AlertTriangle className="h-4 w-4" />,
  Security: <ShieldCheck className="h-4 w-4" />,
  Tests: <FlaskConical className="h-4 w-4" />,
};

import { METADATA_TYPE_TO_ENTITY_TYPE } from "./explorerNodes";

function entityFromNodeParam(node: string | null): EntitySearchItem | null {
  if (!node) return null;
  const [metadataType, ...nameParts] = node.split(":");
  const name = nameParts.join(":").trim();
  if (!metadataType || !name) return null;
  const type = METADATA_TYPE_TO_ENTITY_TYPE[metadataType] ?? "object";
  return {
    type,
    name,
    label: name,
  };
}

export function Explorer() {
  const [searchParams] = useSearchParams();
  const linkedEntity = useMemo(
    () => entityFromNodeParam(searchParams.get("node")),
    [searchParams]
  );
  const [query, setQuery] = useState("");
  const search = useEntitySearch(query);
  const [selected, setSelected] = useState<EntitySearchItem>(linkedEntity ?? {
    type: "org",
    name: "org",
    label: "Org 360",
  });

  useEffect(() => {
    if (!linkedEntity) return;
    setSelected(linkedEntity);
    setQuery(linkedEntity.name);
  }, [linkedEntity]);

  const entity = useEntity360(selected.type, selected.name);
  const results = search.data?.entities ?? [];
  const actionsByPanel = useMemo(() => {
    const groups = new Map<string, EntityAction[]>();
    for (const action of entity.data?.actions ?? []) {
      groups.set(action.panel, [...(groups.get(action.panel) ?? []), action]);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [entity.data?.actions]);

  return (
    <div className="space-y-6 animate-fade-in">
      <TermWindow title="Metadata Explorer" status="Interactive">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              {"Search the org, open metadata, run the right migration, download the result"}
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Smart Metadata Explorer
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Search objects, classes, triggers, and flows. Each entity opens the
              right migration workspace with graph, metadata DB, RAG, Salesforce DX
              MCP, risks, and prefilled actions.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/tools">All tools</Link>
          </Button>
        </div>
      </TermWindow>

      <div className="grid gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <TermWindow title="Search Metadata" status={`${results.length} result${results.length === 1 ? "" : "s"}`}>
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Account, AbortOperations, Flow..."
                className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              />
            </div>
            <button
              type="button"
              onClick={() => setSelected({ type: "org", name: "org", label: "Org 360" })}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left font-mono text-xs ${
                selected.type === "org" ? "bg-foreground text-background" : "hover:bg-muted"
              }`}
            >
              {ENTITY_ICONS.org}
              Org 360
            </button>
            <div className="max-h-[64vh] space-y-1 overflow-auto pr-1">
              {(search.isLoading ? [] : results).map((item) => (
                <button
                  key={`${item.type}:${item.name}`}
                  type="button"
                  onClick={() => setSelected(item)}
                  className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left ${
                    selected.type === item.type && selected.name === item.name
                      ? "bg-foreground text-background"
                      : "hover:bg-muted"
                  }`}
                >
                  <span className="mt-0.5 shrink-0">{ENTITY_ICONS[item.type] ?? ENTITY_ICONS.object}</span>
                  <span className="min-w-0">
                    <span className="block break-words font-mono text-xs">{item.name}</span>
                    <span className="block font-mono text-[10px] opacity-70">{item.type}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </TermWindow>

        <div className="min-w-0 space-y-5">
          <TermWindow
            title="Overview"
            status={entity.isLoading ? "Loading" : entity.isError ? "Issue" : "Ready"}
          >
            {entity.isError ? (
              <p className="font-mono text-sm text-destructive">
                {(entity.error as Error)?.message ?? "failed to load entity"}
              </p>
            ) : (
              <EntityHeader data={entity.data} selected={selected} />
            )}
          </TermWindow>

          <div className="grid gap-5 xl:grid-cols-2">
            <FactsPanel data={entity.data} />
            <RiskPanel data={entity.data} />
          </div>

          <TermWindow title="Recommended Migrations" status={`${entity.data?.actions.length ?? 0} available`}>
            <div className="space-y-4">
              {actionsByPanel.map(([panel, actions]) => (
                <div key={panel}>
                  <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-muted-foreground">
                    {PANEL_ICONS[panel] ?? <Boxes className="h-4 w-4" />}
                    {panel}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {actions.map((action) => (
                      <ActionCard key={action.skill_id} action={action} selected={selected} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TermWindow>
        </div>
      </div>
    </div>
  );
}

function EntityHeader({
  data,
  selected,
}: {
  data: ReturnType<typeof useEntity360>["data"];
  selected: EntitySearchItem;
}) {
  const summary = data?.summary ?? {};
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-muted p-2 text-[hsl(var(--accent-warm))]">
          {ENTITY_ICONS[selected.type] ?? ENTITY_ICONS.object}
        </div>
        <div className="min-w-0">
          <h2 className="break-words text-2xl font-semibold">{selected.name}</h2>
          <p className="font-mono text-xs text-muted-foreground">{selected.type}</p>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        {Object.entries(summary).slice(0, 8).map(([key, value]) => (
          <div key={key} className="rounded-md border border-border bg-card px-3 py-2">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">{key}</div>
            <div className="mt-1 break-words font-mono text-sm">{String(value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FactsPanel({ data }: { data: ReturnType<typeof useEntity360>["data"] }) {
  const facts = data?.facts ?? {};
  return (
    <TermWindow title="Details Found" status="Metadata + graph + MCP">
      <div className="space-y-3">
        {Object.entries(facts).slice(0, 8).map(([key, value]) => {
          const envelope = value as { ok?: boolean; data?: unknown; error?: string };
          const count = Array.isArray(envelope?.data)
            ? envelope.data.length
            : envelope?.data && typeof envelope.data === "object"
              ? Object.keys(envelope.data as Record<string, unknown>).length
              : 0;
          return (
            <div key={key} className="rounded-md border border-border bg-card px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-sm">{key}</span>
                <span className={envelope?.ok === false ? "text-destructive" : "text-success"}>
                  {envelope?.ok === false ? "error" : "ok"}
                </span>
              </div>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {envelope?.ok === false ? envelope.error : `${count} item(s)`}
              </p>
            </div>
          );
        })}
      </div>
    </TermWindow>
  );
}

function RiskPanel({ data }: { data: ReturnType<typeof useEntity360>["data"] }) {
  return (
    <TermWindow title="Things To Review" status={`${data?.risks.length ?? 0} signal${(data?.risks.length ?? 0) === 1 ? "" : "s"}`}>
      <div className="space-y-2">
        {(data?.risks ?? []).map((risk, idx) => (
          <div key={idx} className="rounded-md border border-border bg-card px-3 py-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--accent-warm))]" />
              <span className="font-mono text-sm">{risk.title}</span>
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {risk.severity} {risk.evidence !== undefined ? `· evidence ${String(risk.evidence)}` : ""}
            </p>
          </div>
        ))}
      </div>
    </TermWindow>
  );
}

function ActionCard({ action, selected }: { action: EntityAction; selected: EntitySearchItem }) {
  const params = new URLSearchParams();
  params.set("inputs", encodeBase64Json(action.prefilled_inputs ?? {}));
  params.set("entity_type", selected.type);
  params.set("entity_name", selected.name);
  const href = `/migrations/${encodeURIComponent(action.skill_id)}?${params.toString()}`;
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="break-words font-mono text-sm font-semibold">{action.title}</h3>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{action.description}</p>
        </div>
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {action.trigger}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {action.tools.slice(0, 4).map((tool) => (
          <span key={tool} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {tool}
          </span>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
          <Download className="h-3 w-3" /> md html json pdf
        </span>
        <Button asChild size="sm" variant="charcoal">
          <Link to={href}>Open</Link>
        </Button>
      </div>
    </div>
  );
}
